// Component for SearchBar to be used for both FBOs and Cloudports
import * as React from 'react';
import { theme } from '../../theme/theme';
import SearchIcon from '@mui/icons-material/Search';
import moment from 'moment';

import {
  AppBar,
  Button,
  Container,
  Grid,
  TextField,
  Toolbar,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  InputLabel,
  FormControl
} from '@mui/material';

// This is a helper utility that detects whether any filters have been applied
// in the object returned by the SearchBar
export function HasFilters(filters) {
  if (!filters) {
    return false
  } else if (filters.search && filters.search.length >= 0) {
    return true
  } else if (filters.filterTypes?.length > 0) {
    return true
  } else {
    return false
  }
}

// This is a helper utility that applies the filter object returned by the SearchBar
// to a list of summaries, returning the result.
export function FilterSummaries(summaries, filters) {
  if (!filters || !summaries) {
    return summaries
  }

  const isVPNActive = (time) => {
    return time && moment(time).isAfter(moment().subtract(3, 'minutes'))
  }

  // Do filtering
  let filtered = summaries.filter((s) => {
    let match = true

    // Apply search bar
    // The "minimum 3-characters to search rule" applies in the SearchBar
    // before sending up the filters to the parent. If we've gotten here,
    // we assume we're good to search
    if (filters.search && filters.search.length >= 0) {
      let searchMatch = false
      let lowerSearch = filters.search.toLowerCase()

      searchMatch |= s.serialNumber && s.serialNumber.toLowerCase().includes(lowerSearch)
      searchMatch |= s.customer && s.customer.toLowerCase().includes(lowerSearch)
      searchMatch |= s.shortName && s.shortName.toLowerCase().includes(lowerSearch)
      searchMatch |= s.vpnIP && s.vpnIP.toLowerCase().includes(lowerSearch)

      match &= searchMatch
    }

    // Apply combo box filters
    if (filters.filterTypes && filters.filterTypes.length > 0) {
      let filterMatch = filters.filterTypes.some((t) => {
        switch (t) {
          case 'Error':
            return s.overallDeviceHealth === 'Error'
          case 'Warning':
            return s.overallDeviceHealth === 'Warning'
          case 'Online':
            return s.networkHealth === 'Healthy'
          case 'Offline':
            return s.networkHealth !== 'Healthy'
          case 'Software Out Of Date':
            return s.softwareVersionHealth !== 'Healthy'
          case 'Content Out Of Date':
            return s.visionContentHealth !== 'Healthy'
          case 'VPN Active':
            return isVPNActive(s.lastVPNUpTime)
          case 'VPN Inactive':
            return !isVPNActive(s.lastVPNUpTime)
          default:
            console.warn(`Unknown filter value ${t}`)
        }
      })

      match &= filterMatch
    }

    return match
  })

  // Do sorting
  let sorted = filtered

  const sortAsc = (field) => {
    sorted = sorted.sort((a, b) => {
      if (a[field] < b[field]) {
        return -1
      } else if (a[field] > b[field]) {
        return 1
      } else {
        return 0
      }
    })
  }
  const sortDesc = (field) => {
    sorted = sorted.sort((a, b) => {
      if (a[field] < b[field]) {
        return 1
      } else if (a[field] > b[field]) {
        return -1
      } else {
        return 0
      }
    })
  }

  switch (filters.sortBy) {
    case "CustomerAsc":
      sortAsc("customer")
      break
    case "CustomerDesc":
      sortDesc("customer")
      break
    case "ShortNameAsc":
      sortAsc("shortName")
      break
    case "ShortNameDesc":
      sortDesc("shortName")
      break
    case "LastStatusAsc":
      sortAsc("lastStatusUpdate")
      break
    case "LastStatusDesc":
      sortDesc("lastStatusUpdate")
  }

  return sorted
}


// JS doesn't have interfaces
//  use this commented out definition as a reference for the props that should be accepted for this component 
// export interface SearchBarProps {
//   onSearch : function
// }

const SearchBar = (props) => {

  const [searchText, setSearchText] = React.useState("");
  const [filterTypes, setFilterTypes] = React.useState([]);
  const [sortBy, setSortBy] = React.useState("");

  React.useEffect(() => {
    if (searchText.length === 0 || searchText.length >= 3) {
      notifyFilters()
    }
  }, [searchText])

  React.useEffect(() => {
    notifyFilters()
  }, [filterTypes, sortBy])

  const onTextUpdated = (evt) => {
    setSearchText(evt.target.value);
  };

  const onKeyDown = (evt) => {
    if (evt.keyCode === 13) { // Detect the enter key
      notifyFilters()
    }
  }

  const onFiltersUpdated = (evt) => {
    setFilterTypes(evt.target.value)
  }

  const onSortByUpdated = (evt) => {
    setSortBy(evt.target.value)
  }

  const notifyFilters = () => {
    if (props.onFiltersChanged) {
        props.onFiltersChanged({
          search: searchText,
          filterTypes: filterTypes,
          sortBy: sortBy,
        })
    } else {
        console.error('SearchBar: No onFiltersChanged callback prop defined');
    }
  }

  const onSearchClicked = () => {
    notifyFilters()
  };

  const barTop = props.summaryType === "uncommissioned" ? 80 : 80 + 90;

  return (
    <AppBar position="fixed" sx={{backgroundColor: theme.palette.tertiary.main, top: barTop, height: 64, p: 0}}>
      <Container maxWidth="false">
        <Toolbar disableGutters>
          <Grid container direction='row'>
            <Grid item xs={4}>
                <TextField
                    InputProps={{
                        startAdornment: (
                            <SearchIcon htmlColor='white' />
                        ),
                    }}
                    placeholder='Search by IP, Customer, or Serial #'
                    onChange={onTextUpdated}
                    onKeyDown={onKeyDown} 
                    size='small'
                    variant='outlined'
                    fullWidth
                    sx={{ 
                      "& .MuiOutlinedInput-root":{"& > fieldset": {borderColor: theme.palette.tertiary.contrastText}},
                      input: { 
                        color: theme.palette.tertiary.contrastText,
                        fontSize: '18px',
                        fontWeight: 700
                      }
                    }}
                />
            </Grid>
            <Grid item xs={2}>
                <Button
                    onClick={onSearchClicked}
                    variant='contained'
                    sx={{backgroundColor: 'white', color: theme.palette.tertiary.main, ml: 2, fontSize: '18px', fontWeight: 700}}
                >
                    Search
                </Button>
            </Grid>
            <FormControl xs={2} sx={{marginLeft: "auto", marginRight: "15px", width: 200}} size="small">
              <InputLabel id="filter-type-label" sx={{ color: theme.palette.tertiary.contrastText, fontSize: '18px', fontWeight: 700 }}>Filters</InputLabel>
              <Select
                labelId="filter-type-label"
                value={filterTypes}
                label="Filters"
                multiple
                onChange={onFiltersUpdated}
                renderValue={(selected) => {
                  if (selected.length > 0) {
                    return selected.join(',')
                  } else {
                    return ''
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      fontSize: '18px',
                      fontWeight: 700
                    },
                  },
                }}
                sx={{ 
                  color: theme.palette.tertiary.contrastText,
                  fontSize: '18px',
                  fontWeight: 700,
                  "& .MuiOutlinedInput-notchedOutline": {
                    color: theme.palette.tertiary.contrastText, 
                    borderColor: theme.palette.tertiary.contrastText
                  },
                  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {color: theme.palette.tertiary.contrastText, borderColor: theme.palette.tertiary.contrastText},
                  "& :hover .MuiOutlinedInput-notchedOutline": {color: theme.palette.tertiary.contrastText, borderColor: theme.palette.tertiary.contrastText},
                  "& .MuiSelect-iconOutlined": {color: theme.palette.tertiary.contrastText},
                  input: { 
                    color: theme.palette.tertiary.contrastText,
                    fontSize: '18px',
                    fontWeight: 700
                  }
                }}
              >
                <MenuItem value="Error">
                  <Checkbox checked={filterTypes.indexOf("Error") > -1} sx={{ 
                    color: theme.palette.primary.contrastText, 
                    "&.Mui-checked": { color: theme.palette.primary.contrastText } 
                  }} />
                  <ListItemText primary="Error" />
                </MenuItem>
                <MenuItem value="Warning">
                  <Checkbox checked={filterTypes.indexOf("Warning") > -1} sx={{ 
                    color: theme.palette.primary.contrastText, 
                    "&.Mui-checked": { color: theme.palette.primary.contrastText } 
                  }} />
                  <ListItemText primary="Warning" />
                </MenuItem>
                <MenuItem value="Online">
                  <Checkbox checked={filterTypes.indexOf("Online") > -1} sx={{ 
                    color: theme.palette.primary.contrastText, 
                    "&.Mui-checked": { color: theme.palette.primary.contrastText } 
                  }} />
                  <ListItemText primary="Online" />
                </MenuItem>
                <MenuItem value="Offline">
                  <Checkbox checked={filterTypes.indexOf("Offline") > -1} sx={{ 
                    color: theme.palette.primary.contrastText, 
                    "&.Mui-checked": { color: theme.palette.primary.contrastText } 
                  }} />
                  <ListItemText primary="Offline" />
                </MenuItem>
                <MenuItem value="Software Out Of Date">
                  <Checkbox checked={filterTypes.indexOf("Software Out Of Date") > -1} sx={{ 
                    color: theme.palette.primary.contrastText, 
                    "&.Mui-checked": { color: theme.palette.primary.contrastText } 
                  }} />
                  <ListItemText primary="Software Out Of Date" />
                </MenuItem>
                <MenuItem value="Content Out Of Date">
                  <Checkbox checked={filterTypes.indexOf("Content Out Of Date") > -1} sx={{ 
                    color: theme.palette.primary.contrastText, 
                    "&.Mui-checked": { color: theme.palette.primary.contrastText } 
                  }} />
                  <ListItemText primary="Content Out Of Date" />
                </MenuItem>
                <MenuItem value="VPN Active">
                  <Checkbox checked={filterTypes.indexOf("VPN Active") > -1} sx={{ 
                    color: theme.palette.primary.contrastText, 
                    "&.Mui-checked": { color: theme.palette.primary.contrastText } 
                  }} />
                  <ListItemText primary="VPN Active" />
                </MenuItem>
                <MenuItem value="VPN Inactive">
                  <Checkbox checked={filterTypes.indexOf("VPN Inactive") > -1} sx={{ 
                    color: theme.palette.primary.contrastText, 
                    "&.Mui-checked": { color: theme.palette.primary.contrastText } 
                  }} />
                  <ListItemText primary="VPN Inactive" />
                </MenuItem>
              </Select>
            </FormControl>
            <FormControl xs={1} sx={{ width: 250 }} size="small">
              <InputLabel id="sort-label" sx={{ color: theme.palette.tertiary.contrastText, fontSize: '18px', fontWeight: 700 }}>Sort By</InputLabel>
              <Select
                labelId="sort-label"
                value={sortBy}
                label="Sort By"
                onChange={onSortByUpdated}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      fontSize: '18px',
                      fontWeight: 700
                    },
                  },
                }}
                sx={{ 
                  color: theme.palette.tertiary.contrastText,
                  fontSize: '18px',
                  fontWeight: 700,
                  "& .MuiOutlinedInput-notchedOutline": {
                    color: theme.palette.tertiary.contrastText, 
                    borderColor: theme.palette.tertiary.contrastText
                  },
                  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {color: theme.palette.tertiary.contrastText, borderColor: theme.palette.tertiary.contrastText},
                  "& :hover .MuiOutlinedInput-notchedOutline": {color: theme.palette.tertiary.contrastText, borderColor: theme.palette.tertiary.contrastText},
                  "& .MuiSelect-iconOutlined": {color: theme.palette.tertiary.contrastText},
                  input: { 
                    color: theme.palette.tertiary.contrastText,
                    fontSize: '18px',
                    fontWeight: 700
                  }
                }}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="CustomerAsc">Customer Name A&#8594;Z</MenuItem>
                <MenuItem value="CustomerDesc">Customer Name Z&#8594;A</MenuItem>
                <MenuItem value="ShortNameAsc">Short Name A&#8594;Z</MenuItem>
                <MenuItem value="ShortNameDesc">Short Name Z&#8594;A</MenuItem>
                <MenuItem value="LastStatusAsc">Last Status Earliest&#8594;Latest</MenuItem>
                <MenuItem value="LastStatusDesc">Last Status Latest&#8594;Earliest</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Toolbar>
      </Container>
    </AppBar>
  );
};
export default SearchBar;

